import type { Ingredient } from '../types';

const HEBREW_MULTI_PREFIXES = ['כשה', 'כש', 'שב', 'וב', 'ול', 'מה', 'בה', 'לה', 'מן', 'את'] as const;
const HEBREW_SINGLE_PREFIXES = 'ובלמכהשא' as const;

const ENGLISH_STOP_WORDS = new Set(['the', 'a', 'an', 'of', 'and', 'or', 'to', 'with', 'in', 'on', 'at', 'some']);

function normalizeMatchingText(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['"״׳]/g, '')
        .trim();
}

/** Strip common Hebrew clitic prefixes (ה, ב, ו, …) from a single token. */
export function stripHebrewPrefixes(token: string): string {
    let word = token;
    let changed = true;

    while (changed && word.length > 0) {
        changed = false;

        for (const prefix of HEBREW_MULTI_PREFIXES) {
            if (word.startsWith(prefix) && word.length > prefix.length + 1) {
                word = word.slice(prefix.length);
                changed = true;
                break;
            }
        }

        if (!changed && word.length > 1 && HEBREW_SINGLE_PREFIXES.includes(word[0] as typeof HEBREW_SINGLE_PREFIXES[number])) {
            word = word.slice(1);
            changed = true;
        }
    }

    return word;
}

function stripEnglishPlural(token: string): string {
    if (token.length > 3 && token.endsWith('es')) return token.slice(0, -2);
    if (token.length > 3 && token.endsWith('s')) return token.slice(0, -1);
    return token;
}

function containsHebrew(text: string): boolean {
    return /[\u0590-\u05FF]/.test(text);
}

function tokenizeForMatching(text: string): string[] {
    const normalized = normalizeMatchingText(text);
    if (!normalized) return [];

    return normalized
        .split(/[\s,;:.!?()\-–—/]+/)
        .map((token) => {
            const stripped = containsHebrew(token)
                ? stripHebrewPrefixes(token)
                : stripEnglishPlural(token);
            return stripped.trim();
        })
        .filter((token) => token.length > 0 && !ENGLISH_STOP_WORDS.has(token));
}

function ingredientTokens(name: string): string[] {
    return tokenizeForMatching(name);
}

function consecutiveTokensMatch(needleTokens: string[], haystackTokens: string[]): boolean {
    if (needleTokens.length === 0) return false;

    if (needleTokens.length === 1) {
        const needle = needleTokens[0];
        if (needle.length < 2) return false;

        return haystackTokens.some((token) => {
            if (token === needle) return true;
            if (needle.length >= 3 && token.includes(needle)) return true;
            return false;
        });
    }

    outer:
    for (let i = 0; i <= haystackTokens.length - needleTokens.length; i++) {
        for (let j = 0; j < needleTokens.length; j++) {
            if (haystackTokens[i + j] !== needleTokens[j]) {
                continue outer;
            }
        }
        return true;
    }

    return false;
}

function normalizedIncludes(haystack: string, needle: string): boolean {
    const h = normalizeMatchingText(haystack);
    const n = normalizeMatchingText(needle);
    if (!h || !n) return false;
    if (h.includes(n)) return true;

    const compactHaystack = tokenizeForMatching(haystack).join('');
    const compactNeedle = ingredientTokens(needle).join('');
    return compactNeedle.length >= 3 && compactHaystack.includes(compactNeedle);
}

export function ingredientNameAppearsInStep(ingredientName: string, stepText: string): boolean {
    if (!ingredientName?.trim() || !stepText?.trim()) return false;

    if (normalizedIncludes(stepText, ingredientName)) return true;

    const stepTokens = tokenizeForMatching(stepText);
    const nameTokens = ingredientTokens(ingredientName);
    return consecutiveTokensMatch(nameTokens, stepTokens);
}

export type IngredientMatchOptions = {
    englishIngredients?: ReadonlyArray<{ name?: string }>;
};

export function matchIngredientsInStep(
    stepText: string,
    ingredients: Ingredient[],
    options: IngredientMatchOptions = {},
): Ingredient[] {
    if (!stepText?.trim() || !ingredients?.length) return [];

    const matches = ingredients.filter((ing) => {
        if (!ing.name?.trim()) return false;

        const names = [ing.name];
        const englishIndex = ingredients.indexOf(ing);
        const englishName = options.englishIngredients?.[englishIndex]?.name;
        if (englishName?.trim()) names.push(englishName);

        return names.some((name) => ingredientNameAppearsInStep(name, stepText));
    });

    return matches
        .filter((ing) => !matches.some(
            (other) => other !== ing
                && other.name.length > ing.name.length
                && other.name.toLowerCase().includes(ing.name.toLowerCase()),
        ))
        .sort((a, b) => a.name.localeCompare(b.name));
}
